import '../../App.css';
import CreateApi from '../../shared/Settings/appSettings';
import { localDateStringToUTC } from "@moment-golf/shared";
import {
    Stack, Typography, IconButton, Popover } from '@mui/material';
import React, { useState } from 'react';
import { StyledTextboxInput } from '../../shared/Components/StyledTextboxInput';
import { StyledAutocompleteInput } from '../../shared/Components/StyledAutocompleteInput';
import { StyledIconButton } from '../../shared/Components/StyledIconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'react-oidc-context';
import { useTBContext } from '../Contexts/useTBStates';


interface NewTournamentProps {
    appDims: any;
    queryEventData: boolean;
    setQueryEventData: React.Dispatch<React.SetStateAction<boolean>>;
    /** When provided, the new event is associated with this group. */
    eventGroupID?: string | null;
    /** When provided, called with the created event after successful add (e.g. to add to group.eventIDs). */
    onEventCreated?: (newEvent: any) => void;
    /** When provided, Popover open is controlled by parent (e.g. for "Add Event" from GroupEditor). */
    controlledOpen?: boolean;
    onControlledClose?: () => void;
    /** When provided, used as Popover anchor (e.g. "Add Event" button in GroupEditor). */
    anchorEl?: HTMLElement | null;
}

/** Shape of reference data entries from context (optionsLoaded). */
interface OptionsLoadedItem {
    keyName: string;
    options?: Array<Record<string, unknown>>;
}

/** Shape of user data from context. */
interface UserDataShape {
    displayName?: string;
}

const blankNewTournament = {
    eventName: '',
    courseID: '',
    courseName: '',
    startDate: '',
    endDate: '',
    year: '',
    totalRounds: '',
    playerCap: '',
    playType: '',
    regType: '',
    defaultTeeOrder: '',
    playFormat: '',
    playerVersus: '',
    scoreFormat: '',
    rounds: []
};

export const NewTournament = ({ appDims, queryEventData, setQueryEventData, eventGroupID, onEventCreated, controlledOpen, onControlledClose, anchorEl }: NewTournamentProps) => {

    const auth = useAuth();
    const api = CreateApi();

    const { factor, appTheme, optionsLoaded, viewPanel, userData } = useTBContext();
    const optionsLoadedTyped = optionsLoaded as OptionsLoadedItem[];
    const userDataTyped = userData as UserDataShape | null | undefined;

    const [open, setOpen] = useState(false);

    const [newTournament, setNewTournament] = useState<{
        eventName: string | null;
        courseID: number | string; // to rounds as well
        courseName: string | null; // to rounds as well
        startDate: string | null;
        endDate: string | null;
        year: number | string;
        totalRounds: number | string;
        playerCap: number | string;
        playType: string | null; // to rounds as well
        regType: string | null;
        defaultTeeOrder: string | null; // to rounds as well
        playFormat: string | null; // to rounds as well
        playerVersus: string | null; // to rounds as well
        scoreFormat: string | null; // to rounds as well
        rounds: any[];
    }>(blankNewTournament);

    const validateTournament = () => {
        const hasBlankFields = Object.values(newTournament).some(value => value === '' || value === null || value === undefined);
        console.log(hasBlankFields);
        return !hasBlankFields;
    }

    const handleAddNewTournament = async () => {
        console.log('Add new tournament');

        if (validateTournament()) {
            const startDateUTC = localDateStringToUTC(newTournament.startDate || '');
            const endDateUTC = localDateStringToUTC(newTournament.endDate || '');

            const rounds: any[] = [];
            const playTypeDefaults = optionsLoadedTyped.find((o: any) => o.keyName === 'PlayTypes')?.options?.find(
                (opt: any) => opt.type === newTournament.playType
            )?.defaults as { round?: Record<string, unknown>; event?: Record<string, unknown> } | undefined || null;

            const regTypeDefaults = optionsLoadedTyped.find((o: any) => o.keyName === 'RegTypes')?.options?.find(
                (opt: any) => opt.type === newTournament.regType
            )?.defaults || null;

            // Creates rounds data from event section
            for (let i = 1; i <= Number(newTournament.totalRounds); i++) {
                rounds.push({
                    ...(playTypeDefaults?.round || {}), // first to allow for overwrites
                    roundNumber: i,
                    startHole: 1,
                    // from event to rounds
                    playType: newTournament.playType,
                    teeOrder: newTournament.defaultTeeOrder,
                    courseName: newTournament.courseName,
                    courseID: newTournament.courseID,
                    scoreFormat: newTournament.scoreFormat,
                    playerVersus: newTournament.playerVersus,
                    playFormat: newTournament.playFormat,
                });
            }

            // Creates event data from event section
            const params: Record<string, unknown> = {
                ...(playTypeDefaults?.event || {}), // first to allow for overwrites
                ...(regTypeDefaults || {}), // first to allow for overwrites
                ...newTournament,
                userID: auth.user?.profile.sub,
                ownerName: userDataTyped?.displayName ?? '',
                startDate: startDateUTC,
                endDate: endDateUTC,
                rounds: rounds
            };

            // Removes round only fields from event section
            delete params.playerVersus

            try {
                const payload: any = { ...params };
                if (eventGroupID != null) payload.eventGroupIDs = [eventGroupID];
                const response = await api.post('/tb/AddNewTournament', payload);
                if (onEventCreated && response.data) onEventCreated(response.data);
                setQueryEventData(!queryEventData);
                if (onControlledClose) onControlledClose();
                else setOpen(false);
                setNewTournament(blankNewTournament);
            } catch (error) {
                console.error('Error adding new tournament:', error);
            }
        }
        else {
            window.alert('Please fill in all required fields');
        }
    };

    const isControlled = controlledOpen !== undefined;
    const popoverOpen = isControlled ? controlledOpen : open;
    const handlePopoverClose = () => {
        if (isControlled) onControlledClose?.();
        else setOpen(false);
    };

    // When used as Add Event (controlled), center in viewport; otherwise anchor to button/header
    const centerInViewport = isControlled;
    const viewportCenter = centerInViewport && typeof window !== 'undefined'
        ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
        : undefined;

    return (
        <>
            {viewPanel === 'eventList' && !isControlled && (
            <StyledIconButton
                appTheme={appTheme}
                factor={factor}
                color='primary'
                icon={<AddIcon sx={{ fontSize: 16 * factor }} />}
                label='New'
                width={'max-content'}
                height={28 * factor}
                fill='outlined'
                onClick={() => {
                    setOpen(!open);
                    setNewTournament(blankNewTournament);
                }}
            />
            )}
            <Popover
                open={popoverOpen}
                anchorReference={centerInViewport ? 'anchorPosition' : 'anchorEl'}
                anchorEl={centerInViewport ? undefined : (anchorEl ?? document.getElementById('tournament-builder-header'))}
                anchorPosition={centerInViewport ? viewportCenter : undefined}
                transformOrigin={centerInViewport ? { vertical: 'center', horizontal: 'center' } : undefined}
                anchorOrigin={centerInViewport ? undefined : { vertical: 'bottom', horizontal: 'left' }}
                onClose={handlePopoverClose}
                sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}  
                PaperProps={{
                    sx: {
                        backgroundColor: 'white',
                        margin: centerInViewport ? 'auto' : 'auto 30%',
                        width: '40%',
                        height: '80%',
                        maxHeight: '90vh',
                        padding: '20px 60px',
                        borderRadius: 2,
                        minWidth: '400px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }
                }}
            >
                <Stack direction='column' spacing={0} sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    zIndex: 1000,
                }}>
                    <Stack direction={'row'} spacing={2}
                        sx={{
                            flexShrink: 0,
                            height: 60 * factor,
                            justifyContent: 'flex-end',
                            padding: '0px 20px',
                            alignItems: 'center',
                            borderBottom: `1px dotted ${appTheme.palette.primary.light}`,
                        }}
                    >
                        <Typography flex={2} sx={{ fontSize: 16*factor*1.25 }}>New Tournament Setup</Typography>
                        <StyledIconButton
                            appTheme={appTheme}
                            width={'max-content'}
                            factor={factor}
                            color='primary'
                            icon={<></>}
                            label='Create Tournament'
                            onClick={handleAddNewTournament}
                        />
                        <IconButton color='error' onClick={handlePopoverClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    <Stack
                        direction='column'
                        spacing={2}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            padding: 2,
                            overflowY: 'auto',
                        }}
                    >
                        <Stack direction={'row'} gap={5} sx={{ justifyContent: 'center', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <Stack direction="column" spacing={2} sx={{ flex: 1, minWidth: 300 }}>
                                    <Typography variant="subtitle1" color="primary.main">Tournament Details</Typography>
                                    <StyledTextboxInput
                                        fullWidth
                                        label="Owner"
                                        disabled={true}
                                        value = {auth.user?.profile.sub}
                                        appTheme={appTheme}
                                        error={!auth.user?.profile.sub}
                                    />
                                    <StyledTextboxInput
                                        fullWidth
                                        label="Event Name" 
                                        variant="outlined"
                                        appTheme={appTheme}
                                        value={newTournament.eventName}
                                        onChange={(e) => setNewTournament({...newTournament, eventName: e.target.value})}
                                        error={!newTournament.eventName}
                                    />
                                    <StyledAutocompleteInput    
                                        fullWidth
                                        label="Course Name"
                                        options={optionsLoadedTyped.find((o: any) => o.keyName === 'courses')?.options || []}
                                        getOptionLabel={(option: any) => option.courseName}
                                        appTheme={appTheme}
                                        errorWhenEmpty
                                        value={optionsLoadedTyped.find((o: any) => o.keyName === 'courses')?.options?.find(
                                            (opt: any) => opt.courseName === newTournament.courseName
                                        ) || null}
                                        onChange={(e, newValue) => setNewTournament({...newTournament, courseID: newValue?._id || '', courseName: newValue?.courseName || ''})}
                                    />
                                    <StyledTextboxInput
                                        fullWidth
                                        label="Start Date"
                                        type="date"
                                        variant="outlined"
                                        appTheme={appTheme}
                                        value={newTournament.startDate}
                                        error={!newTournament.startDate}
                                        onChange={(e) => {
                                            setNewTournament({...newTournament, startDate: e.target.value});
                                        }}
                                    />
                                    <StyledTextboxInput
                                        fullWidth
                                        label="End Date"
                                        type="date"
                                        variant="outlined"
                                        appTheme={appTheme}
                                        value={newTournament.endDate}
                                        error={!newTournament.endDate}
                                        onChange={(e) => {
                                            setNewTournament({...newTournament, endDate: e.target.value});
                                        }}
                                    />
                                    <StyledTextboxInput
                                        fullWidth
                                        label="Season / Year"
                                        variant="outlined"
                                        appTheme={appTheme}
                                        value={newTournament.year}
                                        error={!newTournament.year}
                                    onChange={(e) => setNewTournament({...newTournament, year: parseInt(e.target.value)})}
                                    />
                                    <StyledTextboxInput
                                        fullWidth
                                        label="Total Rounds"
                                        variant="outlined"
                                        appTheme={appTheme}
                                        value={newTournament.totalRounds}
                                        onChange={(e) => setNewTournament({...newTournament, totalRounds: parseInt(e.target.value)})}
                                        error={!newTournament.totalRounds}
                                    />
                            </Stack>
                        <Stack direction="column" spacing={2} sx={{ flex: 1, minWidth: 300 }}>
                            <Stack spacing={2}>
                                <Typography variant="subtitle1" color="primary.main">Tournament Format</Typography>
                                <StyledTextboxInput
                                    fullWidth
                                    label="Maximum Players"
                                    type="number"
                                    variant="outlined"
                                    appTheme={appTheme}
                                    value={newTournament.playerCap}
                                    onChange={(e) => setNewTournament({...newTournament, playerCap: parseInt(e.target.value)})}
                                    error={!newTournament.playerCap}
                                />
                                <StyledAutocompleteInput
                                    label="Play Type"
                                    options={optionsLoadedTyped.find((o: any) => o.keyName === 'PlayTypes')?.options || []}
                                    value={optionsLoadedTyped.find((o: any) => o.keyName === 'PlayTypes')?.options?.find(
                                        (opt: any) => opt.type === newTournament.playType
                                    ) || null}
                                    appTheme={appTheme}
                                    errorWhenEmpty
                                    onChange={(e, newValue) => setNewTournament({
                                        ...newTournament, 
                                        playType: newValue?.type || '', 
                                    })}
                                    getOptionLabel={(option: any) => option.type}
                                />
                                <StyledAutocompleteInput
                                    label="Play Format"
                                    options={optionsLoadedTyped.find((o: any) => o.keyName === 'PlayFormats')?.options || []}
                                    value={optionsLoadedTyped.find((o: any) => o.keyName === 'PlayFormats')?.options?.find(
                                        (opt: any) => opt.type === newTournament.playFormat
                                    ) || null}
                                    appTheme={appTheme}
                                    errorWhenEmpty
                                    onChange={(e, newValue) => setNewTournament({
                                        ...newTournament, 
                                        playFormat: newValue?.type || '', 
                                    })}
                                    getOptionLabel={(option: any) => option.type}
                                />
                                <StyledAutocompleteInput
                                    label="Score Format"
                                    options={optionsLoadedTyped.find((o: any) => o.keyName === 'ScoreFormat')?.options || []}
                                    value={optionsLoadedTyped.find((o: any) => o.keyName === 'ScoreFormat')?.options?.find(
                                        (opt: any) => opt.type === newTournament.scoreFormat
                                    ) || null}
                                    appTheme={appTheme}
                                    errorWhenEmpty
                                    onChange={(e, newValue) => setNewTournament({
                                        ...newTournament, 
                                        scoreFormat: newValue?.type || '', 
                                    })}
                                    getOptionLabel={(option: any) => option.type}
                                />
                                <StyledAutocompleteInput
                                    label="Player Versus"
                                    options={optionsLoadedTyped.find((o: any) => o.keyName === 'PlayerVersus')?.options || []}
                                    value={optionsLoadedTyped.find((o: any) => o.keyName === 'PlayerVersus')?.options?.find(
                                        (opt: any) => opt.type === newTournament.playerVersus
                                    ) || null}
                                    appTheme={appTheme}
                                    errorWhenEmpty
                                    onChange={(e, newValue) => setNewTournament({
                                        ...newTournament, 
                                        playerVersus: newValue?.type || '', 
                                    })}
                                    getOptionLabel={(option: any) => option.type}
                                />
                                <StyledAutocompleteInput    
                                    label="Registration Format"
                                    options={optionsLoadedTyped.find((o: any) => o.keyName === 'RegTypes')?.options || []}
                                    value={optionsLoadedTyped.find((o: any) => o.keyName === 'RegTypes')?.options?.find(
                                        (opt: any) => opt.type === newTournament.regType
                                    ) || null}
                                    getOptionLabel={(option: any) => option.type}
                                    appTheme={appTheme}
                                    errorWhenEmpty
                                    onChange={(e, newValue) => setNewTournament({
                                        ...newTournament, 
                                        regType: newValue?.type || ''
                                    })}
                                />
                                <StyledAutocompleteInput
                                    label="Default Tee Order"
                                    options={optionsLoadedTyped.find((o: any) => o.keyName === 'TeeOrder')?.options || []}
                                    value={optionsLoadedTyped.find((o: any) => o.keyName === 'TeeOrder')?.options?.find(
                                        (opt: any) => opt.type === newTournament.defaultTeeOrder
                                    ) || null}
                                    appTheme={appTheme}
                                    errorWhenEmpty
                                    onChange={(e, newValue) => setNewTournament({
                                        ...newTournament, 
                                        defaultTeeOrder: newValue?.type || ''
                                    })}
                                    getOptionLabel={(option: any) => option.type}
                                />
                            </Stack>
                        </Stack>
                        </Stack>
                    </Stack>  
                </Stack>
            </Popover>
        </>
    )
}   